import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, Calendar, Users, Clock, MapPin, Video, GraduationCap } from "lucide-react";
import { UniHeader, fmtDateRange, fmtSessionRange, usd } from "./_lmsShared";

const DELIVERY_LABEL = {
  online_live: { label: "Online · Live", Icon: Video },
  self_paced: { label: "Online · Self-Paced", Icon: Video },
  in_person: { label: "In-Person", Icon: MapPin },
  hybrid: { label: "Hybrid", Icon: Video },
};

export default function UniversityCECalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await universityApi.eventsList({ event_type: "continuing_education", upcoming_only: 0 });
        setEvents(r.data?.items || []);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load CE calendar"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Continuing Education" back="/university" />
      <main className="max-w-6xl mx-auto px-6 py-10" data-testid="uni-ce-calendar">
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
          <Calendar className="w-3.5 h-3.5" />Public CE Calendar
        </div>
        <h1 className="font-display text-4xl text-slate-900">Continuing Education at KISSED University</h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Live, self-paced, and in-person continuing education courses for licensed practitioners. Earn CE hours and grow your practice — all delivered by Cinthia Vonn and KISSED faculty.
        </p>

        {loading ? (
          <div className="mt-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading events…</div>
        ) : events.length === 0 ? (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
            No CE events are scheduled at the moment. Please check back soon.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((e) => {
              const dt = DELIVERY_LABEL[e.delivery_type] || DELIVERY_LABEL.online_live;
              const seatsLeft = e.seats_remaining;
              return (
                <Link key={e.id} to={`/university/events/${e.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-purple-300 transition" data-testid={`uni-ce-card-${e.id}`}>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-purple-900">
                    <dt.Icon className="w-3.5 h-3.5" />{dt.label}
                  </div>
                  <div className="font-display text-lg text-slate-900 mt-1 leading-snug">{e.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{fmtSessionRange(e)}</div>
                  {e.instructor_name && <div className="mt-1 text-xs text-slate-600">Instructor: {e.instructor_name}</div>}
                  <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{seatsLeft == null ? "Open seats" : `${seatsLeft} seats left`}</span>
                    <span className="inline-flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" />{Number(e.ce_hours || 0)} CE hrs · {Number(e.metadata?.clock_hours || e.ce_hours || 0)} clock hrs</span>
                  </div>
                  <div className="mt-1 text-[11px] text-emerald-700 font-medium">
                    {e.metadata?.certificate_eligible === false ? "" : "✓ Certificate Eligible"}
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                    <div className="text-sm font-medium text-slate-900">{usd(e.price_usd)}</div>
                    <div className="text-xs text-purple-900">View & register →</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
